import React, { useState, useEffect, useMemo } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import Loader from "../common/Loader";

// Decode a base64url-encoded UTF-8 string (JWT-style encoding)
const base64UrlDecodeUtf8 = (input) => {
  const b64 = input.replace(/-/g, "+").replace(/_/g, "/");
  const padded = b64 + "=".repeat((4 - (b64.length % 4)) % 4);
  const bytes = Uint8Array.from(atob(padded), (c) => c.charCodeAt(0));
  return new TextDecoder().decode(bytes);
};

// Try to decode the URL param as { id, token }. If it's not a JSON payload,
// fall back to treating the raw param as the invite token itself.
const resolveInviteToken = (encoded) => {
  if (!encoded) return { id: null, token: null };
  try {
    const decoded = JSON.parse(base64UrlDecodeUtf8(encoded));
    if (decoded && typeof decoded.token === "string") {
      return { id: decoded.id ?? null, token: decoded.token };
    }
  } catch {
    // not a base64-JSON payload — fall through
  }
  // Fallback: the URL param IS the token
  return { id: null, token: encoded };
};

const Signup = () => {
  const navigate = useNavigate();
  const { token: tokenParam } = useParams(); // from /signup/:token
  const { signup, checkToken } = useAuth();

  // Decode the invite token once per URL change (no `data` reference, no shadowing)
  const { id, token } = useMemo(
    () => resolveInviteToken(tokenParam),
    [tokenParam],
  );

  // Token-check state
  const [tokenChecking, setTokenChecking] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [tokenError, setTokenError] = useState("");

  // Form state
  const [formData, setFormData] = useState({
    username: "",
    firstname: "",
    lastname: "",
    mobile: "",
    password: "",
    confirmPassword: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [strength, setStrength] = useState({
    length: false,
    upper: false,
    lower: false,
    number: false,
    special: false,
  });

  // =============================================================
  // API CALL #1 — CHECK TOKEN (fires once when component mounts)
  // =============================================================
  useEffect(() => {
    let cancelled = false;

    const runCheckToken = async () => {
      setTokenChecking(true);
      setTokenValid(false);
      setTokenError("");

      if (!token) {
        if (!cancelled) {
          setTokenError("Missing invitation token.");
          setTokenChecking(false);
        }
        return;
      }

      const result = await checkToken(token); // ← GET /v1/SetInProgress?token=...

      if (cancelled) return;

      if (result.success) {
        setTokenValid(true);

        // Optional prefill from API response
        const d = result.data || {};
        setFormData((prev) => ({
          ...prev,
          username: d.email ?? d.username ?? prev.username,
          firstname: d.firstName ?? d.firstname ?? prev.firstname,
          lastname: d.lastName ?? d.lastname ?? prev.lastname,
          mobile: d.mobile ?? prev.mobile,
        }));
      } else {
        setTokenError(
          result.message ||
            "This invitation link is invalid or has expired.",
        );
      }

      setTokenChecking(false);
    };

    runCheckToken();
    return () => {
      cancelled = true;
    };
  }, [token, checkToken]);

  // Password strength
  useEffect(() => {
    setStrength({
      length: formData.password.length >= 9 && formData.password.length <= 128,
      upper: /[A-Z]/.test(formData.password),
      lower: /[a-z]/.test(formData.password),
      number: /[0-9]/.test(formData.password),
      special: /[^A-Za-z0-9]/.test(formData.password),
    });
  }, [formData.password]);

  const strengthScore = Object.values(strength).filter(Boolean).length;
  const strengthLabel = [
    "Very Weak",
    "Weak",
    "Fair",
    "Good",
    "Strong",
    "Strong",
  ][strengthScore];
  const strengthColor = [
    "danger",
    "danger",
    "peach-glow",
    "light-orange",
    "blaze-orange",
    "blaze-orange",
  ][strengthScore];

  const emailRegex = /^[^@\s]+@[^@\s.]+(\.[^@\s.]+)+$/;

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setError("");
  };

  const validateForm = () => {
    if (!emailRegex.test(formData.username)) {
      setError("Username is not a valid email.");
      return false;
    }
    if (!formData.firstname.trim()) {
      setError("First name is required.");
      return false;
    }
    if (formData.firstname.length > 200) {
      setError("First name cannot exceed 200 characters.");
      return false;
    }
    if (!formData.lastname.trim()) {
      setError("Last name is required.");
      return false;
    }
    if (formData.lastname.length > 200) {
      setError("Last name cannot exceed 200 characters.");
      return false;
    }
    if (!formData.mobile.trim()) {
      setError("Mobile number is required.");
      return false;
    }
    if (formData.mobile.length > 100) {
      setError("Mobile number cannot exceed 100 characters.");
      return false;
    }
    if (strengthScore < 5) {
      setError(
        "Password must contain upper and lower case letters, a digit and a symbol.",
      );
      return false;
    }
    if (formData.password !== formData.confirmPassword) {
      setError("Passwords do not match.");
      return false;
    }
    return true;
  };

  // =============================================================
  // API CALL #2 — SIGNUP (fires only when form is submitted)
  // =============================================================
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setMessage("");

    if (!validateForm()) return;

    setLoading(true);

    const { confirmPassword, ...payload } = formData;

    const result = await signup(
      // ← POST /v1/SignUpUser
      payload.username,
      payload.password,
      payload.firstname,
      payload.lastname,
      payload.mobile,
      token, // include invite token in body
    );

    if (result.success) {
      setMessage("Account created successfully! Redirecting to login...");
      setTimeout(() => navigate("/login"), 2000);
    } else {
      setError(result.message);
    }
    setLoading(false);
  };

  // ---------------- RENDER ----------------

  // 1) Token check in progress → spinner
  if (tokenChecking) {
    return (
      <div className="container d-flex align-items-center justify-content-center min-vh-100 py-4">
        <div
          className="card card-shadow p-4 p-md-5 text-center"
          style={{ maxWidth: "480px", width: "100%" }}
        >
          <div className="text-center mb-3">
            <img
              src="/logo/Paynuts-logo-2026.png"
              alt="PayNuts Go"
              style={{ height: "40px" }}
            />
          </div>
          <Loader message="Verifying your invitation link..." size="medium" />
        </div>
      </div>
    );
  }

  // 2) Token invalid → API error only (no form)
  if (!tokenValid) {
    return (
      <div className="container d-flex align-items-center justify-content-center min-vh-100 py-4">
        <div
          className="card card-shadow p-4 p-md-5 text-center"
          style={{ maxWidth: "480px", width: "100%" }}
        >
          <div className="text-center mb-4">
            <img
              src="/logo/Paynuts-logo-2026.png"
              alt="PayNuts Go"
              style={{ height: "40px" }}
            />
          </div>
          <div className="alert alert-danger py-3 mb-3 text-start">
            <div className="fw-bold mb-1">
              <i className="bi bi-exclamation-triangle-fill me-2"></i>
              Invitation problem
            </div>
            <div className="small">{tokenError}</div>
          </div>
          <Link to="/login" className="btn btn-primary w-100">
            Back to Sign In
          </Link>
        </div>
      </div>
    );
  }

  // 3) Token valid → signup form
  return (
    <div className="container d-flex align-items-center justify-content-center min-vh-100 py-4">
      <div
        className="card card-shadow p-4 p-md-5"
        style={{ maxWidth: "480px", width: "100%" }}
      >
        <div className="text-center mb-4">
          <img
            src="/logo/Paynuts-logo-2026.png"
            alt="PayNuts Go"
            className="me-2"
            style={{ height: "40px" }}
          />
          <h4 className="ms-mb-2 text-blaze-orange">Create Account</h4>
          <p className="text-theme-muted small">
            Complete your invitation by creating your account
          </p>
        </div>

        {loading ? (
          <Loader message="Creating your account..." size="medium" />
        ) : (
          <form onSubmit={handleSubmit} noValidate>
            {/* Username */}
            <div className="mb-3">
              <label htmlFor="username" className="form-label">
                Username (Email) <span className="text-danger">*</span>
              </label>
              <input
                type="email"
                id="username"
                name="username"
                className="form-control"
                value={formData.username}
                onChange={handleChange}
                placeholder="test@test.com"
                required
                autoFocus
                disabled={loading}
              />
            </div>

            {/* First Name */}
            <div className="mb-3">
              <label htmlFor="firstname" className="form-label">
                First Name <span className="text-danger">*</span>
              </label>
              <input
                type="text"
                id="firstname"
                name="firstname"
                className="form-control"
                value={formData.firstname}
                onChange={handleChange}
                placeholder="Enter your first name"
                required
                disabled={loading}
                maxLength={200}
              />
            </div>

            {/* Last Name */}
            <div className="mb-3">
              <label htmlFor="lastname" className="form-label">
                Last Name <span className="text-danger">*</span>
              </label>
              <input
                type="text"
                id="lastname"
                name="lastname"
                className="form-control"
                value={formData.lastname}
                onChange={handleChange}
                placeholder="Enter your last name"
                required
                disabled={loading}
                maxLength={200}
              />
            </div>

            {/* Mobile */}
            <div className="mb-3">
              <label htmlFor="mobile" className="form-label">
                Mobile <span className="text-danger">*</span>
              </label>
              <input
                type="tel"
                id="mobile"
                name="mobile"
                className="form-control"
                value={formData.mobile}
                onChange={handleChange}
                placeholder="+61 555 123 4567"
                required
                disabled={loading}
                maxLength={100}
              />
            </div>

            {/* Password */}
            <div className="mb-3">
              <label htmlFor="password" className="form-label">
                Password <span className="text-danger">*</span>
              </label>
              <div className="input-group">
                <input
                  type={showPassword ? "text" : "password"}
                  id="password"
                  name="password"
                  className="form-control"
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="Create a strong password"
                  required
                  disabled={loading}
                  minLength={9}
                  maxLength={128}
                />
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={() => setShowPassword(!showPassword)}
                  tabIndex={-1}
                >
                  <i className={`bi bi-eye${showPassword ? "-slash" : ""}`}></i>
                </button>
              </div>
              {formData.password && (
                <div className="mt-2">
                  <div className="progress" style={{ height: "5px" }}>
                    <div
                      className={`progress-bar bg-${strengthColor}`}
                      style={{ width: `${(strengthScore / 5) * 100}%` }}
                    ></div>
                  </div>
                  <small className={`text-${strengthColor}`}>
                    {strengthLabel}
                  </small>
                </div>
              )}
            </div>

            {/* Confirm Password */}
            <div className="mb-3">
              <label htmlFor="confirmPassword" className="form-label">
                Confirm Password <span className="text-danger">*</span>
              </label>
              <input
                type={showPassword ? "text" : "password"}
                id="confirmPassword"
                name="confirmPassword"
                className="form-control"
                value={formData.confirmPassword}
                onChange={handleChange}
                placeholder="Re-enter your password"
                required
                disabled={loading}
              />
              {formData.confirmPassword &&
                formData.password !== formData.confirmPassword && (
                  <small className="text-danger">Passwords do not match.</small>
                )}
            </div>

            {/* Password requirements */}
            <ul className="list-unstyled small mb-3">
              <li
                className={
                  strength.length ? "text-blaze-orange" : "text-theme-muted"
                }
              >
                <i
                  className={`bi ${strength.length ? "bi-check-circle-fill" : "bi-circle"} me-2`}
                ></i>
                At least 9 characters
              </li>
              <li
                className={
                  strength.upper ? "text-blaze-orange" : "text-theme-muted"
                }
              >
                <i
                  className={`bi ${strength.upper ? "bi-check-circle-fill" : "bi-circle"} me-2`}
                ></i>
                One uppercase letter
              </li>
              <li
                className={
                  strength.lower ? "text-blaze-orange" : "text-theme-muted"
                }
              >
                <i
                  className={`bi ${strength.lower ? "bi-check-circle-fill" : "bi-circle"} me-2`}
                ></i>
                One lowercase letter
              </li>
              <li
                className={
                  strength.number ? "text-blaze-orange" : "text-theme-muted"
                }
              >
                <i
                  className={`bi ${strength.number ? "bi-check-circle-fill" : "bi-circle"} me-2`}
                ></i>
                One number
              </li>
              <li
                className={
                  strength.special ? "text-blaze-orange" : "text-theme-muted"
                }
              >
                <i
                  className={`bi ${strength.special ? "bi-check-circle-fill" : "bi-circle"} me-2`}
                ></i>
                One special character
              </li>
            </ul>

            {message && (
              <div className="alert alert-success py-2">{message}</div>
            )}
            {error && <div className="alert alert-danger  py-2">{error}</div>}

            <button
              type="submit"
              className="btn btn-primary w-100"
              disabled={loading}
            >
              {loading ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2"></span>
                  Creating account...
                </>
              ) : (
                "Create Account"
              )}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default Signup;